import { MenuItem } from './menu-item';
import { composeTenantPath } from './path-compose';

/**
 * Filtragem final da árvore: mantém itens com `show`, compõe o path dos itens
 * de tenant (omitindo-os quando não há identificador), remove itens sem
 * submenu visível e sem `path` próprio, e preserva a ordem. Não muta a árvore
 * de entrada — sempre devolve novos objetos/arrays.
 *
 * A composição de path de tenant só se aplica na profundidade 1 (filhos
 * diretos de um item de topo), espelhando `prepararMenusTenant` do Angular
 * original — que só lê `item.tenant` dentro do `submenu` de cada item de
 * topo, nunca no item de topo em si. Fora dessa profundidade, `tenant` não
 * tem efeito e o item segue o ramo normal. A regra de "descartar quando
 * submenu ficou vazio e path é vazio" continua se aplicando recursivamente
 * em todos os níveis (o Angular original só filtrava o segundo nível; aqui
 * a mesma regra vale até o terceiro nível, para não deixar itens não
 * navegáveis na árvore).
 *
 * Diferente do Angular original — que mantém o item na lista marcado como
 * `show = false` — aqui o item é removido da árvore resultante: simplifica o
 * consumo pelos componentes React, que não precisam checar `show` de novo.
 */
export function filterMenuTree(
  tree: readonly MenuItem[],
  identifierTenant: string | undefined,
): MenuItem[] {
  return filterLevel(tree, identifierTenant, 0);
}

function filterLevel(
  items: readonly MenuItem[],
  identifierTenant: string | undefined,
  depth: number,
): MenuItem[] {
  const result: MenuItem[] = [];

  for (const item of items) {
    if (!item.show) {
      continue;
    }

    if (item.tenant && depth === 1) {
      if (!identifierTenant) {
        continue;
      }

      result.push({
        ...item,
        path: composeTenantPath(item.path, identifierTenant),
        submenu: filterLevel(item.submenu, identifierTenant, depth + 1),
      });
      continue;
    }

    const submenu = filterLevel(item.submenu, identifierTenant, depth + 1);
    if (submenu.length === 0 && item.path.length === 0) {
      continue;
    }

    result.push({ ...item, submenu });
  }

  return result;
}
